import React, {
  createContext,
  useCallback,
  useContext,
  useEffect
} from 'react';
import _get from 'lodash/get';

import Form from './Form';
import { useForm, useMutation, useRetailer, useSegment } from 'utils/hooks';
import { AuthContext } from 'context/AuthContext';
import { DEFAULT_FORM_DATA, FORM_SCHEMA } from './stubs';

/**
 * @type {React.Context<{
 *  data: import("zod").z.infer<typeof FORM_SCHEMA>,
 *  errors: Partial<Record<keyof import("zod").z.infer<typeof FORM_SCHEMA>, string>>,
 *  onChange: (updates: Partial<import("zod").z.infer<typeof FORM_SCHEMA>>, config?: import('utils/types').FormValidationConfig) => void,
 *  isDirty: boolean,
 *  isProcessing: boolean,
 *  onSave: (onDone?: (err: Error, data: any) => void) => void
 * }}}
 */
const ReturnModeConfigContext = createContext();

export function useReturnModeConfig() {
  const context = useContext(ReturnModeConfigContext);
  if (!context) {
    throw new Error(
      'useReturnModeConfig must be used within a ReturnModeConfigProvider'
    );
  }
  return context;
}

export function ReturnModeConfigProvider({ children }) {
  const { track } = useSegment();
  const { returnsMode } = useRetailer();

  const { updateState } = useContext(AuthContext);

  const {
    data,
    errors,
    isDirty,
    onChange,
    onValidate,
    onSetInitialData
  } = useForm({
    defaultData: DEFAULT_FORM_DATA,
    dataSchema: FORM_SCHEMA
  });

  useEffect(() => {
    onSetInitialData({ mode: returnsMode });
  }, [returnsMode, onSetInitialData]);

  const { mutate, isProcessing } = useMutation('returnMode.put', 'PUT', {
    payload: () => ({ mode: data.mode }),
    errorMessage: 'Failed to save the preference. Please try again.',
    onValidate: () => onValidate(data),
    onSuccess: data => {
      track('FTU Wizard Return Mode Configured', { mode: data.mode });
      const storeProperties = _get(data, 'store_properties', {});
      updateState({ storeProperties });
    }
  });

  const onSave = useCallback(callback => mutate({}, callback), [mutate]);

  return (
    <ReturnModeConfigContext.Provider
      value={{
        data,
        errors,
        isDirty,
        isProcessing,
        onChange,
        onSave
      }}
    >
      <Form />
      {children}
    </ReturnModeConfigContext.Provider>
  );
}
